import * as React from 'react';
import { translate } from 'components/translate-hoc';
import { ConfigContext } from 'modules/context';
// components
import Button, { IButtonProps } from 'components/button';
import Spinner from 'components/spinner';
// service
import AnalyticsService from 'modules/analytics-provider/analytics-provider.service';
import { getTrackingFlowSubType } from 'utils/analytics';
// constants
import {
    EMAIL_VERIFICATION,
    EMAIL_VERIFICATION_ACTIONS,
    EMAIL_VERIFICATION_DETAILS_SENDING,
    EMAIL_VERIFICATION_DETAILS_SENT,
    EMAIL_VERIFICATION_FLOW_STEP,
    TEXT_KEYS
} from 'modules/email-verification/email-verification.constants';
import { CATEGORY } from 'modules/analytics-provider/analytics-provider.constants';
// types
import { IEmailProps } from './email-verification.types';
// styles
import './email-verification.styles.less';

export class EmailVerification extends React.PureComponent<IEmailProps> {
    public async componentDidMount() {
        const {
            checkIsEmailVerified,
            checkEmailVerification
        } = this.props;

        const isEmailVerified = await checkIsEmailVerified();
        if (!isEmailVerified) {
            const { config: { widgetName } } = this.context;
            AnalyticsService.sendFlowEvent({
                ...EMAIL_VERIFICATION_FLOW_STEP,
                flowSubType: getTrackingFlowSubType(widgetName),
                action: EMAIL_VERIFICATION_ACTIONS.PROMPTING,
                category: CATEGORY.DISPLAY
            });
        }

        return checkEmailVerification();
    }

    public handleCancelClick = (): void => {
        const { setCompletedVerificationStatus } = this.props;
        const { config: { widgetName } } = this.context;

        AnalyticsService.sendFlowEvent({
            ...EMAIL_VERIFICATION_FLOW_STEP,
            flowSubType: getTrackingFlowSubType(widgetName),
            action: EMAIL_VERIFICATION_ACTIONS.SKIP,
            category: CATEGORY.INTERACTION
        });

        return setCompletedVerificationStatus();
    };

    public handleVerifyEmailClick = async (): Promise<void> => {
        const { verifyEmail, checkIsEmailVerified, setSentVerificationStatus } = this.props;
        const isEmailVerified = await checkIsEmailVerified();
        const { config: { widgetName } } = this.context;

        AnalyticsService.sendFlowEvent({
            ...EMAIL_VERIFICATION_FLOW_STEP,
            flowSubType: getTrackingFlowSubType(widgetName),
            action: EMAIL_VERIFICATION_ACTIONS.VERIFY,
            category: CATEGORY.INTERACTION
        });

        if (isEmailVerified) {
            setSentVerificationStatus();
        } else {
            verifyEmail(); // sending email
        }

        AnalyticsService.sendFlowEvent({
            ...EMAIL_VERIFICATION_FLOW_STEP,
            ...EMAIL_VERIFICATION_DETAILS_SENT,
            flowSubType: getTrackingFlowSubType(widgetName),
            action: EMAIL_VERIFICATION_ACTIONS.SENT,
            category: CATEGORY.DISPLAY
        });
    };

    public handleContinueClick = (): void => {
        const { setCompletedVerificationStatus } = this.props;
        const { config: { widgetName } } = this.context;

        AnalyticsService.sendFlowEvent({
            ...EMAIL_VERIFICATION_FLOW_STEP,
            ...EMAIL_VERIFICATION_DETAILS_SENDING,
            flowSubType: getTrackingFlowSubType(widgetName),
            action: EMAIL_VERIFICATION_ACTIONS.CONTINUE,
            category: CATEGORY.ACTION
        });

        return setCompletedVerificationStatus();
    };

    public onChangeSuggestionClick = () => {
        const { config: { widgetName } } = this.context;
        AnalyticsService.sendFlowEvent({
            ...EMAIL_VERIFICATION_FLOW_STEP,
            flowSubType: getTrackingFlowSubType(widgetName),
            action: EMAIL_VERIFICATION_ACTIONS.USE_ANOTHER,
            category: CATEGORY.ACTION
        });
    };

    public getButtonContainer(): JSX.Element {
        const translateKey = this.props.dangerouslyTranslateWithHTML;
        const cancelButtonText: string = translateKey(TEXT_KEYS.EMAIL_VERIFICATION_CANCEL_BTN);

        const getOkButtonParams = (): object => {
            const promptingProps: Partial<IButtonProps> = { onClick: this.handleVerifyEmailClick };
            const sendingProps: Partial<IButtonProps> = { disabled: true, loading: true };
            const sentProps: Partial<IButtonProps> = { onClick: this.handleContinueClick };

            switch (this.props.emailVerificationStatus) {
            case EMAIL_VERIFICATION.NOT_COMPLETED:
                return promptingProps;
            case EMAIL_VERIFICATION.SENDING:
                return sendingProps;
            case EMAIL_VERIFICATION.SENT:
                return sentProps;
            default:
                return promptingProps;
            }
        };

        return (
            <div className="sitegen-email-verification__button-container">
                <Button {...getOkButtonParams()}>
                    {this.getButtonText()}
                </Button>
                <Button onClick={this.handleCancelClick} type="secondary">
                    <span>{cancelButtonText}</span>
                </Button>
            </div>
        );
    }

    public getButtonText(): string | null {
        const {
            dangerouslyTranslateWithHTML: translateKey,
            emailVerificationStatus
        } = this.props;

        const statusOptions = {
            [EMAIL_VERIFICATION.NOT_COMPLETED]:
                translateKey(TEXT_KEYS.EMAIL_VERIFICATION_OK_BTN_VERIFY),
            [EMAIL_VERIFICATION.SENT]:
                translateKey(TEXT_KEYS.EMAIL_VERIFICATION_OK_BTN_CONTINUE),
        };

        return emailVerificationStatus && statusOptions[emailVerificationStatus] || null;
    }

    public getTextContainer(): JSX.Element {
        const translateKey = this.props.dangerouslyTranslateWithHTML;
        const textWarning: string = translateKey(TEXT_KEYS.EMAIL_VERIFICATION_TEXTS.warning, {
            emailForVerification: '%(email)'
        });
        const textLinking: string = translateKey(TEXT_KEYS.EMAIL_VERIFICATION_TEXTS.linking);
        const textPrompting: string = translateKey(TEXT_KEYS.EMAIL_VERIFICATION_TEXTS.prompting);
        const textSending: string = translateKey(TEXT_KEYS.EMAIL_VERIFICATION_TEXTS.sending);
        const textSent: string = translateKey(TEXT_KEYS.EMAIL_VERIFICATION_TEXTS.sent);

        // Handle click on resend verification link in translated string
        const handleStatusClick = (event: React.SyntheticEvent) => {
            event.preventDefault();
            event.stopPropagation();
            if ((event.target as HTMLAnchorElement).id === 'send-email') {
                const { config: { widgetName } } = this.context;
                AnalyticsService.sendFlowEvent({
                    ...EMAIL_VERIFICATION_FLOW_STEP,
                    ...EMAIL_VERIFICATION_DETAILS_SENDING,
                    flowSubType: getTrackingFlowSubType(widgetName),
                    action: EMAIL_VERIFICATION_ACTIONS.SEND_ANOTHER,
                    category: CATEGORY.INTERACTION
                });
                this.props.verifyEmail();
            }
        };
        const emailVerificationTextStatus = (): string => {
            switch (this.props.emailVerificationStatus) {
            case EMAIL_VERIFICATION.NOT_COMPLETED:
                return textPrompting;
            case EMAIL_VERIFICATION.SENDING:
                return textSending;
            case EMAIL_VERIFICATION.SENT:
                return textSent;
            default:
                return textPrompting;
            }
        };

        return (
            <div className="sitegen-email-verification__text-container">
                <h2 className="sitegen-email-verification__title">{textWarning}</h2>
                <p>{textLinking}</p>
                <p onClick={handleStatusClick}>{emailVerificationTextStatus()}</p>
            </div>
        );
    }

    public renderSpinner() {
        const { getModeClass } = this.context;
        return (
            <div className={`${getModeClass('sitegen-email-verification')} sitegen-email-verification__spinner-container`}>
                <Spinner/>
            </div>
        );
    }

    public render(): JSX.Element {
        const {
            dangerouslyTranslateWithHTML: translateKey,
            emailVerificationStatus
        } = this.props;

        if (!emailVerificationStatus) {
            return this.renderSpinner();
        }

        const { getModeClass } = this.context;

        return (
            <div className={getModeClass('sitegen-email-verification')}>
                <div className="sitegen-email-verification__content">
                    {this.getTextContainer()}
                    {this.getButtonContainer()}

                    <div
                        className="sitegen-email-verification__email-change-suggestion"
                        onClick={this.onChangeSuggestionClick}
                    >
                        {translateKey(TEXT_KEYS.EMAIL_VERIFICATION_EMAIL_CHANGE_SUGGESTION)}
                    </div>
                </div>
            </div>
        );
    }
}

EmailVerification.contextType = ConfigContext;

export default translate()(EmailVerification);
